import { DIMENSION_AVATAR_VERY_SMALL } from '../../../constants';
import type { UserInfo } from '../../../types/UserInfoTypes';
import { AvatarPicture } from '../../avatar/AvatarPicture';
import UserSummaryBasic from '../../common/invite-users/UserSummaryBasic';
import { getIsUserInactive } from '../../../lib/getIsUserActive';
import { useMemo, useRef } from 'react';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { colors, Typography } from '@sweep-io/sweep-design';
import { ChevronDown } from '@sweep-io/sweep-design/dist/icons/ChevronDown';
import WithNotificationDot from '../../common/WithNotificationDot';
import { isEllipsisActive } from '../../../lib/isEllipsisActive';
import { useAccountSelectionContext } from '../../account-selection/AccountSelectionContext';

interface UserSummaryProps {
  user?: UserInfo;
  isActive?: boolean;
  hasUnreadNotifications?: boolean;
}

const UserSummaryForHeader = ({
  user = {},
  isActive,
  hasUnreadNotifications,
}: UserSummaryProps) => {
  const { status } = user || {};
  const avatar = useMemo(() => ({ emoji: user.emoji, imageUrl: user.imageUrl }), [user]);
  const nameRef = useRef<HTMLSpanElement>(null);
  const accountRef = useRef<HTMLSpanElement>(null);
  const isNameEllipsisActive = isEllipsisActive(nameRef.current);
  const isAccountEllipsisActive = isEllipsisActive(accountRef.current);
  const hasEllipsis = isNameEllipsisActive || isAccountEllipsisActive;
  const { accounts } = useAccountSelectionContext();
  const hasManyAccounts = accounts && accounts.length > 1;

  return (
    <UserSummaryBasic
      className={`header-user-summary ${isActive ? 'active' : ''}`}
      sx={{
        backgroundColor: isActive ? colors.grey[100] : undefined,
        '&:hover': { backgroundColor: colors.grey[100] },
        p: '4px 8px',
        borderRadius: '8px',
        maxWidth: '200px',
        overflow: 'hidden',
        minWidth: 0,
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div className="user-summary-avatar" data-testid="user-summary-avatar-header">
        <Stack alignItems="center" justifyContent="center" direction="row" gap={1} minWidth={0}>
          <WithNotificationDot showDot={hasUnreadNotifications ?? false}>
            <AvatarPicture
              avatar={avatar}
              dimension={DIMENSION_AVATAR_VERY_SMALL}
              clickable={true}
              isInactive={getIsUserInactive(status)}
            />
          </WithNotificationDot>

          <Stack direction="row" alignItems="center" justifyContent="center" gap={1} minWidth={0}>
            {hasManyAccounts && (
              <Tooltip
                disableHoverListener={!hasEllipsis}
                title={
                  <Stack>
                    <Typography variant="caption" color={colors.white}>
                      {user.name ?? user.email}
                    </Typography>

                    <Typography variant="caption" color={colors.grey[500]}>
                      Account: {user.account?.name}
                    </Typography>
                  </Stack>
                }
              >
                <Stack maxWidth="135px">
                  <Stack minWidth={0} width="100%">
                    <Typography ref={nameRef} variant="caption" color={colors.grey[900]} ellipsis>
                      {user.name ?? user.email}
                    </Typography>
                    <Typography
                      ref={accountRef}
                      variant="caption"
                      color={colors.grey[700]}
                      ellipsis
                    >
                      {user.account?.name}
                    </Typography>
                  </Stack>
                </Stack>
              </Tooltip>
            )}
            <ChevronDown size="small" color={colors.grey[700]} />
          </Stack>
        </Stack>
      </div>
    </UserSummaryBasic>
  );
};

export { UserSummaryForHeader };
